import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, Lock, Users, HeartHandshake, Check, Star, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SignatureSoundControl from "@/components/SignatureSoundControl";
import SocialLinks from "@/components/SocialLinks";
import { useSeo } from "@/lib/seo";
import { track } from "@/lib/api";
import { AUTH } from "@/constants/testIds";

const HERO_MAIN = "https://images.unsplash.com/photo-1600144559281-53d1dee79a99?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const HERO_SUB = "https://images.unsplash.com/photo-1707914883484-03115dea98fb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const HERO_WIDE = "https://images.unsplash.com/photo-1578513492882-c2a46b45af17?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const INCLUSION_IMAGES = [
  { src: "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Smiling Black woman with curly hair" },
  { src: "https://images.unsplash.com/photo-1561309518-5c3e965995ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "White woman in a sunlit field, warm and natural" },
  { src: "https://images.unsplash.com/photo-1707409066859-a90674383d19?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "A joyful group of women of different backgrounds laughing together" },
];

const FAQS = [
  { q: "Who is Sisterhood+ for?", a: "Sisterhood+ is a space for women from all backgrounds. We believe women share many experiences, while also recognizing that culture, race, family, identity, and lived experience can shape how we move through the world. That's why you'll find both experiences designed for women broadly and culturally responsive collections created around particular communities and experiences." },
  { q: "Why are there collections created for specific communities and experiences?", a: "Because wellness isn't experienced in a vacuum. Culture, family, race, migration, community expectations and other experiences can influence the challenges women face. These collections allow Sisterhood+ to explore those experiences with greater depth while keeping the broader platform open to all women." },
  { q: "Can I explore a collection that isn't specifically about my identity?", a: "Yes. Sisterhood+ encourages curiosity, learning and connection. Some content may speak directly to a particular lived experience, but members are welcome to explore the broader library with respect for the communities and perspectives being centered." },
  { q: "Do I have to choose a race or ethnicity to use Sisterhood+?", a: "No. Your experience is yours to define. Sisterhood+ should never require you to fit neatly into a category in order to belong or access the broader platform." },
  { q: "What if I identify with more than one community?", a: "That's completely welcome. Identity can be layered and complex. You may find meaning in several Sisterhood+ collections, and you don't have to choose only one." },
  { q: "Is Sisterhood+ therapy?", a: "No. Sisterhood+ is a wellness, personal growth, education and community platform. It is not a substitute for therapy, diagnosis, medical care, crisis services or other professional treatment." },
  { q: "What makes Sisterhood+ different?", a: "Sisterhood+ doesn't assume there is one universal experience of womanhood. We bring together tools for the experiences many women share while making room for culture, identity, family, community and the stories that shape each of us." },
];

const TRUST = [
  { label: "For women from every background" },
  { label: "Warm, substantive AI coaching — never clinical" },
  { label: "Private by design — your journal stays yours" },
  { label: "Evidence-informed growth & wellness tools" },
  { label: "A safe, moderated community of sisters" },
  { label: "Culturally responsive spaces for the stories that shape us" },
];

const PILLARS = [
  { title: "Personalized guidance", body: "An AI coach that meets you like a wise, honest sister — warm, direct, and never patronizing." },
  { title: "Practical, everyday tools", body: "Short lessons on confidence, boundaries, motherhood, money, rest — the things that actually run our lives." },
  { title: "A circle that holds you", body: "Safe, moderated community circles and guided growth plans so you never do the work alone." },
];

const WHY = [
  { k: "Seen", v: "So many women carry the invisible load — holding everyone together while quietly running on empty. Sisterhood+ was built to see you, not add to your to-do list." },
  { k: "Substantive", v: "Not another pastel self-care app. Real lessons, real reflection, and coaching with depth — because women don't need a less serious product." },
  { k: "Yours", v: "A private space to learn, reflect, and grow at your own pace — with experiences for what we share and thoughtful spaces for the stories that make us different." },
];

const FEATURES = [
  "A full learning library", "AI Coach whenever you need it", "Daily wellness check-ins",
  "Private journaling + guided prompts", "Guided Growth Plans", "Progress & milestones",
  "Community circles", "Calming tools (breathing & grounding)",
];

function Pricing({ navigate }) {
  const [annual, setAnnual] = useState(false);
  const price = annual ? "99.99" : "14.99";
  const period = annual ? "/year" : "/month";
  const sub = annual ? "Save 44% · best value" : "Cancel anytime";
  return (
    <section id="pricing" className="bg-white border-t border-[#291B2D]/5">
      <div className="max-w-[1100px] mx-auto px-6 py-20">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-4">Membership</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light leading-tight">
            One membership.<br/><span className="italic text-[#291B2D]/60">Your whole becoming.</span>
          </h2>
        </div>
        <div className="mt-10 flex justify-center">
          <div className="inline-flex items-center bg-white border border-[#291B2D]/10 rounded-full p-1 relative shadow-sm" data-testid="landing-billing-toggle">
            <div className={`absolute top-1 bottom-1 rounded-full bg-[#291B2D] transition-all duration-300 ${annual ? "left-1/2 right-1 ml-0.5" : "left-1 right-1/2 mr-0.5"}`} />
            <button onClick={() => setAnnual(false)} data-testid="landing-billing-monthly"
              className={`relative z-10 px-8 py-2.5 text-sm font-medium rounded-full ${!annual ? "text-white" : "text-[#291B2D]/60"}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} data-testid="landing-billing-annual"
              className={`relative z-10 px-8 py-2.5 text-sm font-medium rounded-full ${annual ? "text-white" : "text-[#291B2D]/60"}`}>Annual</button>
          </div>
        </div>
        <div className="mt-12 max-w-md mx-auto">
          <div data-testid="landing-plan" className="relative rounded-[2rem] p-8 lg:p-10 bg-[#291B2D] text-white shadow-[0_20px_60px_rgba(41,27,45,0.28)] ring-2 ring-[#C89B55]/70">
            {annual && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C89B55] text-[#291B2D] text-[10px] uppercase tracking-[0.2em] font-semibold rounded-full px-4 py-1.5 flex items-center gap-1.5 whitespace-nowrap">
                <Star size={12} fill="currentColor" /> Best value
              </span>
            )}
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-[#C89B55]">Sisterhood+</p>
            <h3 className="font-heading text-2xl mt-1">Everything you need to grow.</h3>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="text-lg text-white/55">$</span>
              <span className="font-heading text-6xl font-light tracking-tight" data-testid="landing-price">{price}</span>
              <span className="text-sm ml-1 text-white/60">{period}</span>
            </div>
            <p className="text-xs mt-1 text-white/60">{sub}</p>
            <ul className="mt-6 space-y-2.5">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm"><Check size={16} strokeWidth={2.25} className="mt-0.5 shrink-0 text-[#C89B55]" /><span className="text-white/90">{f}</span></li>
              ))}
            </ul>
            <button onClick={() => { track("get_started_clicked", { from: "pricing" }); navigate("/get-started"); }}
              data-testid="landing-plan-cta"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] transition-smooth">
              Get Started <ArrowRight size={14} />
            </button>
          </div>
          <p className="text-center text-xs text-[#291B2D]/50 mt-6 leading-relaxed">Prices in USD. We never sell your data. Payment is a demo for now.</p>
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a, open, onToggle, idx }) {
  return (
    <div className="bg-white rounded-2xl card-soft overflow-hidden">
      <button onClick={onToggle} data-testid={`faq-q-${idx}`} aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-[#F7F2EA]/50 transition-smooth">
        <span className="font-heading text-lg leading-snug">{q}</span>
        <ChevronDown size={20} className={`shrink-0 text-[#C89B55] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 -mt-1" data-testid={`faq-a-${idx}`}>
          <p className="text-[15px] text-[#291B2D]/70 leading-[1.7]">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function Welcome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);

  useSeo({
    title: "Sisterhood+ | Women's Emotional Wellness App",
    description:
      "Sisterhood+ is a women's emotional wellness app — inclusive of all women, created with women of color in mind. Guided journaling, daily emotional check-ins, self-reflection, and a therapist directory. Start your 7-day free trial.",
    path: "/",
  });

  useEffect(() => { track("landing_viewed"); }, []);

  // Always show the landing page first. While the session is still being checked
  // (user === undefined) show a neutral placeholder to avoid a flash; once resolved,
  // render the landing for everyone (logged in or not).
  if (user === undefined) {
    return <div className="min-h-screen bg-[#FCFAF6]" data-testid="landing-auth-check" />;
  }

  const goToApp = () => navigate(user?.onboarding_completed ? "/home" : "/onboarding");

  return (
    <div className="min-h-screen bg-[#FCFAF6] text-[#291B2D] animate-fade-in">
      <SignatureSoundControl />
      {/* Header */}
      <header className="max-w-[1280px] mx-auto flex items-center justify-between px-6 py-6" data-testid="landing-header">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-[#291B2D] text-white flex items-center justify-center font-heading text-xl">S<span className="text-[#C89B55]">+</span></span>
          <span className="font-heading text-2xl tracking-tight sh-goldshine">Sisterhood<span className="text-[#C89B55]">+</span></span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => { track("directory_clicked", { source: "nav" }); navigate("/therapists"); }} data-testid="nav-find-therapist"
            className="hidden sm:inline text-sm text-[#291B2D]/70 hover:text-[#291B2D] transition-smooth">Find a Therapist</button>
          <a href="#pricing" className="hidden sm:inline text-sm text-[#291B2D]/70 hover:text-[#291B2D] transition-smooth">Pricing</a>
          {user ? (
            <button onClick={goToApp} data-testid="nav-continue-app"
              className="bg-[#291B2D] text-white text-sm rounded-full px-4 py-2 hover:bg-[#352238] transition-smooth">Continue</button>
          ) : (
            <button onClick={() => navigate("/login")} data-testid={AUTH.signInButton}
              className="bg-[#291B2D] text-white text-sm rounded-full px-4 py-2 hover:bg-[#352238] transition-smooth">Sign In</button>
          )}
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1280px] mx-auto px-6 pt-6 sm:pt-10 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#C89B55] font-semibold mb-6"><Sparkles size={14} /> Sisterhood+</div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-[68px] tracking-tight font-light leading-[1.03]">
            Wellness that sees<br/><span className="italic font-normal">the whole woman.</span>
          </h1>
          <p className="mt-6 text-[#291B2D]/70 text-lg leading-relaxed max-w-xl">
            A space for women to reflect, grow, learn, and connect—with experiences designed for what we share and thoughtful spaces for the stories that make us different.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button onClick={() => { track("get_started_clicked", { intent: "trial" }); navigate("/get-started"); }} data-testid={AUTH.createAccountButton}
              className="inline-flex items-center justify-center gap-2 bg-[#291B2D] text-white rounded-full px-8 py-4 font-medium hover:bg-[#352238] transition-smooth shadow-[0_10px_30px_rgba(41,27,45,0.25)]">
              Start your 7-day free trial <ArrowRight size={16} />
            </button>
            <button onClick={() => { track("explore_clicked"); document.getElementById("inclusion")?.scrollIntoView({ behavior: "smooth" }); }} data-testid="landing-explore"
              className="inline-flex items-center justify-center gap-2.5 bg-white border border-[#291B2D]/10 rounded-full px-8 py-4 font-medium hover:bg-[#F7F2EA] transition-smooth">
              Explore Sisterhood+
            </button>
          </div>
          <p className="mt-3 text-xs text-[#291B2D]/50">7 days free, then $14.99/mo. Cancel anytime.</p>
          <button onClick={() => { track("assessment_clicked"); navigate("/assessment"); }} data-testid="landing-free-assessment"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#291B2D] hover:text-[#C89B55] transition-smooth">
            <Sparkles size={15} className="text-[#C89B55]" /> Take the Free Wellness Check-In — no account needed <ArrowRight size={14} />
          </button>
          <button onClick={() => { track("directory_clicked"); navigate("/therapists"); }} data-testid="landing-therapist-directory"
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#291B2D] hover:text-[#C89B55] transition-smooth">
            <Sparkles size={15} className="text-[#C89B55]" /> Browse our Find a Therapist directory — free <ArrowRight size={14} />
          </button>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="trust-indicators">
            {TRUST.map((t, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-[#291B2D]/70"><Check className="text-[#3D6B52] mt-0.5 shrink-0" size={16} strokeWidth={2} /><span>{t.label}</span></div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6 relative">
          <div className="absolute -inset-6 bg-[#C89B55]/10 rounded-[3rem] blur-3xl -z-0" />
          <div className="relative grid grid-cols-2 gap-3 h-[560px]">
            <img src={HERO_MAIN} alt="A woman in a grounded moment of reflection" className="rounded-[2rem] object-cover w-full h-full card-soft" />
            <img src={HERO_SUB} alt="A confident woman in warm light" className="rounded-[2rem] object-cover w-full h-full card-soft" />
          </div>
        </div>
      </section>

      {/* ONE SISTERHOOD — INCLUSION */}
      <section id="inclusion" className="bg-[#291B2D] text-white">
        <div className="max-w-[1280px] mx-auto px-6 py-24">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.24em] text-[#C89B55] font-semibold mb-4">One Sisterhood. Different Stories.</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light leading-[1.1]">
              Womanhood connects us,<br/><span className="text-white/55">but it doesn't make our experiences identical.</span>
            </h2>
            <p className="mt-6 text-white/75 text-lg leading-relaxed">
              Culture. Race. Family. Faith. Immigration. Career. Motherhood. Relationships. Identity. They can all shape how we experience the world—and how we care for ourselves.
            </p>
            <p className="mt-4 text-white/75 text-lg leading-relaxed">
              That's why Sisterhood+ provides experiences for women broadly while also creating spaces that speak more intentionally to particular communities and lived experiences.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {INCLUSION_IMAGES.map((im, i) => (
              <div key={i} className="relative rounded-[2rem] overflow-hidden h-72 card-soft">
                <img src={im.src} alt={im.alt} loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: "50% 30%" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#291B2D]/40 to-transparent" />
              </div>
            ))}
          </div>
          <p className="mt-12 font-heading text-2xl sm:text-3xl font-light text-[#C89B55]">You don't have to fit one story to belong here.</p>
        </div>
      </section>
      <section id="why" className="bg-white border-y border-[#291B2D]/5">
        <div className="max-w-[1280px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-4">Why Sisterhood+</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light leading-tight">You deserve a space<br/><span className="text-[#291B2D]/50">that takes you seriously.</span></h2>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {WHY.map((s, i) => (
              <div key={i} className="bg-[#F7F2EA] rounded-3xl p-10 h-full">
                <p className="font-heading text-3xl font-light">{s.k}</p>
                <p className="text-[15px] text-[#291B2D]/70 mt-6 leading-[1.7]">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-[1280px] mx-auto px-6 py-20 grid md:grid-cols-3 gap-6">
        {PILLARS.map((p, i) => (
          <div key={i} className="bg-white rounded-3xl p-8 card-soft">
            <div className="w-10 h-10 rounded-full bg-[#F7F2EA] flex items-center justify-center text-[#C89B55] font-heading text-lg mb-4">{i + 1}</div>
            <h3 className="font-heading text-2xl mb-3">{p.title}</h3>
            <p className="text-[#291B2D]/70 text-sm leading-relaxed">{p.body}</p>
          </div>
        ))}
      </section>

      {/* EMOTIONAL BEAT */}
      <section className="max-w-[1280px] mx-auto px-6 pb-20">
        <div className="relative rounded-[2.5rem] overflow-hidden card-soft">
          <img src={HERO_WIDE} alt="A woman journaling in soft morning light" className="w-full h-[420px] object-cover" style={{ objectPosition: "50% 30%" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#291B2D]/95 via-[#291B2D]/70 to-[#291B2D]/10" />
          <div className="absolute inset-0 flex items-end p-10 sm:p-14">
            <div className="max-w-xl text-white">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-3">For every version of you</p>
              <h3 className="font-heading text-3xl sm:text-4xl font-light leading-tight">The mother. The partner. The professional. The woman still becoming.</h3>
              <p className="text-white/80 mt-4 max-w-md">Sisterhood+ meets you where you are — and helps you build where you're going.</p>
            </div>
          </div>
        </div>
      </section>

      <Pricing navigate={navigate} />

      {/* FAQ */}
      <section id="faq" className="bg-white border-t border-[#291B2D]/5">
        <div className="max-w-[820px] mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-4">Questions</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light leading-tight">Everyone is welcome.<br/><span className="text-[#291B2D]/50">Everyone doesn't have to be the same.</span></h2>
          </div>
          <div className="space-y-3" data-testid="landing-faq">
            {FAQS.map((f, i) => (
              <FaqItem key={i} idx={i} q={f.q} a={f.a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-[1280px] mx-auto px-6 py-16 text-center">
        <h3 className="font-heading text-4xl sm:text-5xl tracking-tight font-light">Your growth. Your peace. Your future.</h3>
        <p className="text-[#291B2D]/60 mt-4 max-w-xl mx-auto">Start free. No judgment. Just a private space to become more of who you already are.</p>
        <button onClick={() => { track("get_started_clicked", { from: "final-cta" }); navigate("/get-started"); }}
          className="mt-8 inline-flex items-center gap-2 bg-[#C89B55] text-[#291B2D] rounded-full px-8 py-4 font-semibold hover:bg-[#B38743] transition-smooth">
          Get Started <ArrowRight size={16} />
        </button>
      </section>

      <footer className="border-t border-[#291B2D]/5 py-10 text-center text-xs text-[#291B2D]/50 space-y-4" data-testid="landing-footer">
        <SocialLinks />
        <p>Sisterhood+ · Not a substitute for professional care · If you are in crisis, call or text <a href="tel:988" className="text-[#291B2D] underline">988</a></p>
        <p className="text-[#291B2D]/60 font-medium text-center">© 2026 Renewing Minds Counseling and Consulting Services. All rights reserved.</p>
      </footer>
    </div>
  );
}
